import { hybridEncrypt } from "@wingspanhq/utils/dist/crypto";
import { useMemo } from "react";

import { useAccountAccess } from "@hooks/useAccountAccess";
import { tokenStore } from "@services/tokenStore";
import { useSession } from "../../../v3/hooks/useSession";
import { IS_DEV_ENV } from "../../../shared/constants/environment";
import { themeConfig } from "../../../shared/flatfile/themeConfig";

/**
 * Space metadata for the server-side `payablesImport` Flatfile space.
 *
 * The contract is the `payablesImport/README.md` in `flatfile-platform`
 * (WIN-15867). The listener decrypts `apiToken` with its own private key and
 * sends it as `Authorization: Bearer` (`shared/utils/accessToken.ts`), and
 * spreads `customHeaders` onto every call it makes (`shared/utils/api.ts`).
 *
 * Deliberately separate from `useFlatfileSpaceConfig`, which serves the v1
 * `payables`/`contractors` spaces: those listeners call the v1 API and need a v1
 * `ws:auth_token`, which v3 routes reject — and vice versa. The two cannot share
 * one builder until the v1 spaces are retired.
 */
export const usePayablesImportSpaceMetadata = (batchId: string) => {
  const { accountId } = useSession();
  const { isImpersonatedByWingspanAdmin } = useAccountAccess();

  return useMemo(() => {
    const publicKey = (
      process.env.REACT_APP_ENCRYPTION_PUBLIC_KEY as string
    ).replace(/\\n/g, "\n");

    // v3 sessions are account-bound, so the listener resolves the acting
    // account from the token itself. An impersonation token is the exception:
    // v3 refuses it without the header naming the target account.
    const isImpersonating = tokenStore.isImpersonating();
    const accessToken = tokenStore.getAccessToken();

    return {
      ...(accessToken
        ? { apiToken: hybridEncrypt(publicKey, accessToken) }
        : {}),
      batchId,
      customHeaders: {
        ...(isImpersonating && accountId
          ? { "X-Wingspan-Account": accountId }
          : {})
      },
      sidebarConfig: {
        showSidebar: isImpersonatedByWingspanAdmin || IS_DEV_ENV
      },
      ...themeConfig
    };
  }, [accountId, batchId, isImpersonatedByWingspanAdmin]);
};
